import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  Banner,
  ConfirmDialog,
  Icon,
  PageFrame,
  PageHeader,
  Progress,
  StatusChip,
  WS_COLOR,
  WS_RADIUS,
  WorkspaceThemeProvider,
  icons,
} from '@platform/ui-kit/workspace';
import { api, type AirFitProjectGroup, type AirFitmentStandard } from '../../../api';
import { isWorkspaceUi } from '../../shared/workspaceUi';

/**
 * Strategic Intelligence → AI Platform Fitness Analysis, as a Decision Workspace screen.
 *
 * Shown with `?ui=workspace` next to the ported original in `AiPlatformFit.tsx`, which stays the
 * default until the owner approves this one (CLAUDE.md 1.2).
 *
 * WHAT IS UNCHANGED: the API calls (load, run, poll, stop), the 1.2 s first poll and 2 s repeat,
 * one-after-another analysis, selection by project or by opportunity (a project tick covers only
 * the rows a filter leaves visible), the filter fields, the run rules (needs a selection, locked
 * criteria and at least one criterion), every warning and its wording, the "Measured against"
 * line and its show/hide, the executive-summary pane and its three states, the run-status wording
 * (running, stopped, failed, done), the Stop confirmation and what it says, and the two-column
 * arrangement: selection on the left, summary and status on the right.
 *
 * WHAT CHANGES, by the design system and nothing else: the page frame (locked header and toolbar;
 * the two columns scroll on their own), warnings as banners, project rows as disclosure buttons
 * (the original's were clickable divs), the browser `confirm()` as the standard dialog, Material
 * Icons and emoji as Lucide, analysed / not analysed as a status chip, and the progress bar as a
 * labelled one.
 */

const SURFACE = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  minWidth: 0,
  border: `1px solid ${WS_COLOR.border}`,
  borderRadius: `${WS_RADIUS.card}px`,
  backgroundColor: WS_COLOR.surface,
} as const;

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box component="section" aria-label={title} sx={SURFACE}>
      <Typography
        variant="h2"
        component="h2"
        sx={{ flex: 'none', px: 4, py: 3, borderBottom: `1px solid ${WS_COLOR.border}` }}
      >
        {title}
      </Typography>
      <Box tabIndex={0} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 4 }}>
        {children}
      </Box>
    </Box>
  );
}

export function AiPlatformFitWorkspace() {
  return (
    <WorkspaceThemeProvider baseline={false}>
      <Screen />
    </WorkspaceThemeProvider>
  );
}

function Screen() {
  const { search } = useLocation();
  // Links to other screens stay in the workspace version when this one is.
  const ui = isWorkspaceUi(search);
  const [groups, setGroups] = useState<AirFitProjectGroup[]>([]);
  const [criteriaLocked, setCriteriaLocked] = useState(true);
  const [standard, setStandard] = useState<AirFitmentStandard | null>(null);
  const [showStandard, setShowStandard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<{
    status: string;
    total: number;
    done: number;
    current: string | null;
    error: string | null;
    failedOn: string | null;
  } | null>(null);
  const [stopping, setStopping] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [picked, setPicked] = useState<AirFitProjectGroup['opps'][number] | null>(null);

  const toggleOpen = (pid: string) =>
    setExpanded((cur) => {
      const n = new Set(cur);
      if (n.has(pid)) n.delete(pid);
      else n.add(pid);
      return n;
    });

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const load = useCallback(() => {
    setLoading(true);
    api.aiReadiness
      .getPlatformFit()
      .then((d) => {
        setGroups(d.projectGroups ?? []);
        setCriteriaLocked(d.criteriaLocked);
        setStandard(d.standard ?? null);
        setError(null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        opps: g.opps.filter(
          (o) =>
            o.opportunity_id.toLowerCase().includes(q) ||
            o.description.toLowerCase().includes(q) ||
            g.project_id.toLowerCase().includes(q) ||
            g.description.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.opps.length);
  }, [groups, filter]);

  const toggleOpp = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  /* Ticking a project selects only the opportunities currently VISIBLE under the filter. */
  const toggleProject = (g: AirFitProjectGroup, on: boolean) =>
    setSelected((s) => {
      const n = new Set(s);
      g.opps.forEach((o) => (on ? n.add(o.opportunity_id) : n.delete(o.opportunity_id)));
      return n;
    });

  const poll = useCallback(
    (id: string) => {
      api.aiReadiness
        .getFitStatus(id)
        .then((d) => {
          setJob(d);
          if (d.status === 'running') {
            timer.current = setTimeout(() => poll(id), 2000);
          } else {
            setStopping(false);
            load(); // scores and summaries have changed
          }
        })
        .catch(() => {
          timer.current = setTimeout(() => poll(id), 2000);
        });
    },
    [load],
  );

  const run = () => {
    const ids = [...selected];
    if (!ids.length) return;
    setError(null);
    api.aiReadiness
      .runFit(ids)
      .then((d) => {
        setJobId(d.jobId);
        setJob({
          status: 'running',
          total: d.total,
          done: 0,
          current: null,
          error: null,
          failedOn: null,
        });
        timer.current = setTimeout(() => poll(d.jobId), 1200);
      })
      .catch((e: Error) => setError(e.message));
  };

  const stop = () => {
    setConfirmStop(false);
    if (!jobId) return;
    setStopping(true);
    api.aiReadiness.stopFit(jobId).catch((e: Error) => {
      setStopping(false);
      setError(e.message);
    });
  };

  const pickedProject = picked
    ? (groups.find((g) => g.opps.some((o) => o.opportunity_id === picked.opportunity_id))
        ?.project_id ?? '')
    : '';

  const running = job?.status === 'running';
  const jobText = job
    ? job.status === 'running'
      ? `Analyzing ${job.done + 1} of ${job.total}${job.current ? ` — ${job.current}` : ''}`
      : job.status === 'cancelled'
        ? `Stopped. ${job.done} of ${job.total} completed and kept; the one in flight was discarded.`
        : job.status === 'error'
          ? `Failed on ${job.failedOn}: ${job.error}`
          : `Done — ${job.done} of ${job.total} analyzed.`
    : '';

  const warnings = (
    <Box sx={{ display: 'grid', gap: 2, pb: 3 }}>
      {!criteriaLocked ? (
        <Banner tone="warning">
          The <strong>Fitment Criteria are not locked</strong>. Set and lock them on the{' '}
          <a
            href={`/ai_readiness/settings/ai-platform-selection/platform-fitment-criteria${ui ? '?ui=workspace' : ''}`}
          >
            Platform Fitment Criteria
          </a>{' '}
          screen before running an analysis — every analysis uses those locked criteria.
        </Banner>
      ) : null}
      {standard && !standard.criteria.length ? (
        <Banner tone="warning">
          No criteria are selected on the <strong>Platform Capabilities</strong> tab, so there is
          nothing to score a platform against.
        </Banner>
      ) : null}
      {standard && standard.criteria.length && !standard.clientProfile.trim() ? (
        <Banner tone="warning">
          No client environment profile is set on the <strong>Organizational Readiness</strong> tab,
          so every platform scores 50 on that axis — {standard.weightReadiness}% of each overall
          score.
        </Banner>
      ) : null}
      {error ? <Banner tone="error">{error}</Banner> : null}
    </Box>
  );

  return (
    <>
      <PageFrame
        header={<PageHeader title="AI Platform Fitness Analysis" />}
        toolbar={
          <>
            <Box
              role="search"
              sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', py: 3 }}
            >
              <TextField
                placeholder="Filter by project or opportunity…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                inputProps={{ 'aria-label': 'Filter by project or opportunity' }}
                sx={{ flex: '0 1 320px', minWidth: 200 }}
              />
              <Box sx={{ flex: 1 }} />
              <Typography variant="body2" role="status" sx={{ color: WS_COLOR.textMuted }}>
                {selected.size} selected
              </Typography>
              <Button
                variant="contained"
                onClick={run}
                startIcon={<Icon icon={icons.Gauge} size={16} />}
                disabled={
                  !selected.size || running || !criteriaLocked || !(standard?.criteria.length ?? 0)
                }
              >
                Run AI Platform analysis
              </Button>
              {running ? (
                <Button variant="tonal" onClick={() => setConfirmStop(true)} disabled={stopping}>
                  {stopping ? 'Stopping…' : 'Stop analysis'}
                </Button>
              ) : null}
            </Box>
            {warnings}
            {standard && standard.criteria.length ? (
              <Box sx={{ pb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Typography variant="body2">
                    Measured against <strong>Platform Fitment Criteria</strong> —{' '}
                    {standard.criteria.length} criteri{standard.criteria.length === 1 ? 'on' : 'a'}{' '}
                    · {standard.weightCapability}% capability / {standard.weightReadiness}%
                    readiness
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    aria-expanded={showStandard}
                    onClick={() => setShowStandard((v) => !v)}
                  >
                    {showStandard ? 'Hide' : 'Show'}
                  </Button>
                </Box>
                {showStandard ? (
                  <Box
                    sx={{
                      mt: 2,
                      p: 4,
                      border: `1px solid ${WS_COLOR.border}`,
                      borderRadius: `${WS_RADIUS.field}px`,
                      maxHeight: 220,
                      overflowY: 'auto',
                    }}
                    tabIndex={0}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      PLATFORM CAPABILITIES — weighted 1–5
                    </Typography>
                    <Box component="ul" sx={{ mt: 0.5, mb: 2, pl: 5 }}>
                      {standard.criteria.map((c) => (
                        <li key={c.id}>
                          <b>{c.weight}</b> {c.name}
                        </li>
                      ))}
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      ORGANIZATIONAL READINESS
                    </Typography>
                    <Box component="ul" sx={{ mt: 0.5, mb: 2, pl: 5 }}>
                      {standard.readinessFactors.map((f) => (
                        <li key={f.name}>{f.name}</li>
                      ))}
                    </Box>
                    {standard.clientProfile.trim() ? (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          CLIENT ENVIRONMENT PROFILE
                        </Typography>
                        <Typography>{standard.clientProfile}</Typography>
                      </>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </>
        }
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'grid',
            gap: 4,
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            gridTemplateRows: 'minmax(0, 1fr)',
            '@container (max-width: 899px)': {
              gridTemplateColumns: 'minmax(0, 1fr)',
              gridTemplateRows: 'minmax(0, 1fr) minmax(0, 1fr)',
            },
          }}
        >
          <Card title="Project / Opportunities selection">
            {loading ? (
              <Typography role="status" sx={{ color: WS_COLOR.textMuted }}>
                Loading…
              </Typography>
            ) : !visible.length ? (
              <Typography sx={{ color: WS_COLOR.textMuted }}>
                {groups.length
                  ? 'No project or opportunity matches that filter.'
                  : 'No opportunities found.'}
              </Typography>
            ) : (
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 3 }}>
                {visible.map((g) => {
                  const analyzed = g.opps.filter((o) => o.analyzed).length;
                  const all = g.opps.every((o) => selected.has(o.opportunity_id));
                  const some = !all && g.opps.some((o) => selected.has(o.opportunity_id));
                  const open = expanded.has(g.project_id);
                  const listId = `fit-opps-${g.project_id}`;
                  return (
                    <Box
                      component="li"
                      key={g.project_id}
                      sx={{
                        border: `1px solid ${WS_COLOR.border}`,
                        borderRadius: `${WS_RADIUS.field}px`,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          pr: 3,
                          backgroundColor: WS_COLOR.surfaceSubtle,
                          borderRadius: `${WS_RADIUS.field}px`,
                        }}
                      >
                        <Checkbox
                          checked={all}
                          indeterminate={some}
                          onChange={(e) => toggleProject(g, e.target.checked)}
                          inputProps={{ 'aria-label': `Select all of ${g.project_id}` }}
                        />
                        <Box
                          component="button"
                          type="button"
                          aria-expanded={open}
                          aria-controls={listId}
                          onClick={() => toggleOpen(g.project_id)}
                          sx={{
                            all: 'unset',
                            boxSizing: 'border-box',
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            minHeight: 44,
                            cursor: 'pointer',
                            '&:focus-visible': {
                              outline: `2px solid ${WS_COLOR.primary}`,
                              outlineOffset: 2,
                            },
                          }}
                        >
                          <Icon icon={open ? icons.ChevronDown : icons.ChevronRight} size={16} />
                          <Typography component="span" sx={{ fontWeight: 600 }}>
                            {g.project_id}
                          </Typography>
                          {g.description ? (
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ color: WS_COLOR.textMuted, minWidth: 0 }}
                              noWrap
                            >
                              — {g.description}
                            </Typography>
                          ) : null}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 'auto', flex: 'none', color: WS_COLOR.textMuted }}
                          >
                            {g.opps.length} opp{g.opps.length === 1 ? '' : 's'}
                            {analyzed ? ` · ${analyzed} analyzed` : ''}
                          </Typography>
                        </Box>
                      </Box>

                      {open ? (
                        <Box id={listId} component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                          {g.opps.map((o) => (
                            <Box
                              component="li"
                              key={o.opportunity_id}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                borderTop: `1px solid ${WS_COLOR.border}`,
                              }}
                            >
                              <Checkbox
                                checked={selected.has(o.opportunity_id)}
                                onChange={() => toggleOpp(o.opportunity_id)}
                                inputProps={{ 'aria-label': `Select ${o.opportunity_id}` }}
                              />
                              <Box
                                component="button"
                                type="button"
                                aria-current={
                                  picked?.opportunity_id === o.opportunity_id ? 'true' : undefined
                                }
                                onClick={() => setPicked(o)}
                                sx={{
                                  all: 'unset',
                                  boxSizing: 'border-box',
                                  flex: 1,
                                  minWidth: 0,
                                  py: 2,
                                  pr: 3,
                                  cursor: 'pointer',
                                  '&:focus-visible': {
                                    outline: `2px solid ${WS_COLOR.primary}`,
                                    outlineOffset: 2,
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 2,
                                  }}
                                >
                                  <Typography component="span" sx={{ fontWeight: 600 }}>
                                    {o.opportunity_id}
                                  </Typography>
                                  <StatusChip
                                    tone={o.analyzed ? 'success' : 'neutral'}
                                    label={o.analyzed ? 'Analyzed' : 'Not analyzed'}
                                  />
                                  {o.analyzed && o.bestPlatform ? (
                                    <StatusChip tone="info" label={`✓ ${o.bestPlatform}`} />
                                  ) : null}
                                  {o.overallScore != null ? (
                                    <Typography component="span" variant="caption">
                                      Overall {o.overallScore}
                                    </Typography>
                                  ) : null}
                                  {o.fitScore != null ? (
                                    <Typography component="span" variant="caption">
                                      Fit {o.fitScore}
                                    </Typography>
                                  ) : null}
                                  {o.readinessScore != null ? (
                                    <Typography component="span" variant="caption">
                                      Ready {o.readinessScore}
                                    </Typography>
                                  ) : null}
                                </Box>
                                {o.description ? (
                                  <Typography
                                    variant="body2"
                                    sx={{ color: WS_COLOR.textMuted, mt: 0.5 }}
                                  >
                                    {o.description}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Card>

          <Card title="Executive summary & run status">
            {!picked ? (
              <Typography sx={{ color: WS_COLOR.textMuted }}>
                Select an opportunity on the left to read its executive summary.
              </Typography>
            ) : !picked.execSummary ? (
              <Typography sx={{ color: WS_COLOR.textMuted }}>
                Opportunity {picked.opportunity_id} has not been analyzed yet. Select it and run the
                analysis.
              </Typography>
            ) : (
              <Box>
                <Typography variant="h3" component="h3">
                  ★ Best fit — {picked.bestPlatform}{' '}
                  <Box component="span" sx={{ color: WS_COLOR.textMuted, fontWeight: 400 }}>
                    ({picked.opportunity_id})
                  </Box>
                </Typography>
                {picked.overallScore != null ||
                picked.fitScore != null ||
                picked.readinessScore != null ? (
                  <Box sx={{ display: 'flex', gap: 5, my: 3 }}>
                    {picked.overallScore != null ? (
                      <ScoreFact value={picked.overallScore} label="Overall" />
                    ) : null}
                    {picked.fitScore != null ? (
                      <ScoreFact value={picked.fitScore} label="Requirement fit" />
                    ) : null}
                    {picked.readinessScore != null ? (
                      <ScoreFact value={picked.readinessScore} label="Adoption readiness" />
                    ) : null}
                  </Box>
                ) : null}
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{picked.execSummary}</Typography>
                <Typography variant="body2" sx={{ mt: 3, color: WS_COLOR.textMuted }}>
                  Full 6-section analysis (incl. adoption considerations) is on the{' '}
                  <a
                    href={`/ai_readiness/strategic-intelligence/platform-fit-summary?open=${encodeURIComponent(
                      pickedProject,
                    )}&opp=${encodeURIComponent(picked.opportunity_id)}${ui ? '&ui=workspace' : ''}`}
                  >
                    Platform Fit Summary
                  </a>{' '}
                  screen.
                </Typography>
              </Box>
            )}

            {job ? (
              <Box sx={{ mt: 5 }} role="status">
                <Progress
                  label={jobText}
                  value={job.total ? Math.round((job.done / job.total) * 100) : 0}
                />
              </Box>
            ) : null}
          </Card>
        </Box>
      </PageFrame>

      <ConfirmDialog
        open={confirmStop}
        title="Stop the AI platform analysis?"
        tone="destructive"
        confirmLabel="Stop analysis"
        cancelLabel="Keep running"
        onConfirm={stop}
        onClose={() => setConfirmStop(false)}
      >
        The initiative currently being analyzed will be discarded and NOT saved. Everything already
        completed is kept.
      </ConfirmDialog>
    </>
  );
}

function ScoreFact({ value, label }: { value: number; label: string }) {
  return (
    <Box>
      <Typography component="div" sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography component="div" variant="caption" sx={{ color: WS_COLOR.textMuted }}>
        {label}
      </Typography>
    </Box>
  );
}
